/**
 * Order-level "Customer Notes" card, rendered beneath the Location section on
 * OrderDetails. Consolidates the three pieces of customer-declared order info
 * that used to be split across two surfaces:
 *   - Dump description (what the customer is disposing of)
 *   - Additional info (optional free-text)
 *   - Prohibited-items acknowledgment (the reservation gate)
 *
 * The first two previously lived in OrderRightSidebar's "Customer Notes"
 * (placement) section; the third was its own card here. They're one semantic
 * cluster — what the customer told us about what's going in the bin — and they
 * already share the queryKeys.orders.localRow(orderLocalId!) query + the same
 * source-gating, so merging collapses two query consumers into one.
 *
 * Editable-vs-audit is decided by order source:
 *  - Admin/phone-in orders (source === 'admin'): editable. Staff capture the
 *    info on the customer's behalf. Text fields autosave on blur; the ack is a
 *    checkbox + phone script (ProhibitedItemsAck) and is what unblocks reserve.
 *  - Website orders: read-only record of the customer's checkout submission.
 *  - Other / sync orders with nothing recorded: renders nothing.
 *
 * The dump description + additional info read as notes; the prohibited-items
 * ack reads as a gate (required asterisk + "can't be reserved" warning) and is
 * kept in its own sub-block below a divider so the gate stays weightier than
 * the free-text above it.
 */
import React from 'react';
import {
  Text,
  Textarea,
  Tooltip,
  Toaster,
  Toast,
  ToastTitle,
  ToastBody,
  Spinner,
  useId,
  useToastController,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
  DismissCircle16Filled,
  Info16Regular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../lib/apiFetch';
import { queryKeys } from '../lib/queryClient';
import { fetchOrderLocalRow } from '../services/orderService';
import ProhibitedItemsAck from './ProhibitedItemsAck';

const useStyles = makeStyles({
  // The card mirrors the Customer/Location/Billing envelope surfaces: a labeled
  // white surface holding the stacked fields.
  card: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
    ...shorthands.padding('16px', '24px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
  },
  cardTitle: {
    marginBottom: '-4px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
  },
  fieldValue: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
  },
  fieldValueEmpty: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  textarea: {
    width: '100%',
  },
  // Divider sets the prohibited-items gate apart from the free-text notes above
  // it, so the reservation gate doesn't read as "just another note".
  divider: {
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.margin('0'),
  },
  ackBlock: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
  },
  ackHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    flexWrap: 'wrap',
  },
  // Pending acknowledgment marker: a required-field red asterisk + an info icon
  // whose tooltip carries the "can't be reserved until confirmed" reason, so the
  // header stays quiet instead of spelling the whole consequence out.
  requiredAsterisk: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 700,
  },
  infoIcon: {
    color: tokens.colorNeutralForeground3,
    cursor: 'help',
    display: 'inline-flex',
  },
  // Website orders: the box was checked at checkout, so render it as a quiet
  // audit footnote rather than an attention-grabbing status field.
  auditNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  auditIcon: {
    color: tokens.colorNeutralForeground4,
    flexShrink: 0,
  },
  notAcked: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
});

interface PlacementData {
  debris_description?: string | null;
  prohibited_items_acknowledged?: boolean | null;
  notes?: string | null;
  source?: string | null;
}

export interface OrderCustomerNotesSectionProps {
  /** TWM order uuid (present for any saved order, promoted or not). Null for a
   *  not-yet-saved new order, where there's nothing to capture yet. */
  orderLocalId: string | null;
  /** Force the read-only display (dispatched/completed order behind the
   *  accidental-edit guard), even for an admin-source order that would
   *  otherwise be editable. */
  locked?: boolean;
}

const OrderCustomerNotesSection: React.FC<OrderCustomerNotesSectionProps> = ({
  orderLocalId,
  locked = false,
}) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const toasterId = useId('order-customer-notes-toaster');
  const { dispatchToast } = useToastController(toasterId);

  // Shares the local-row cache (queryKeys.orders.localRow) with OrderDetails and
  // the right sidebar so this gate doesn't fire its own GET /orders/:id. The
  // PlacementData fields (source, notes, debris_description, ack) are a subset
  // of the full row, so reads and the optimistic ack merge below stay valid.
  const placementQuery = useQuery<{ data?: PlacementData }>({
    queryKey: queryKeys.orders.localRow(orderLocalId!),
    queryFn: () => fetchOrderLocalRow(orderLocalId!),
    enabled: !!orderLocalId,
    staleTime: 30 * 1000,
  });

  const placement = placementQuery.data?.data;
  const debrisDescription = (placement?.debris_description || '').trim();
  const additionalInfo = (placement?.notes || '').trim();
  const acknowledged = placement?.prohibited_items_acknowledged;
  // Editable only for admin-created orders — the phone-in case staff fill in on
  // the customer's behalf. Website orders are the customer's own submission and
  // booqable-sync orders are left alone.
  const editable = !!orderLocalId && placement?.source === 'admin' && !locked;

  // Editable drafts for the two free-text fields, seeded once from the server.
  // The ref guard stops a post-save refetch from clobbering in-flight edits
  // (same idiom OrderRightSidebar used for tags/waive-fee).
  const [debrisDraft, setDebrisDraft] = React.useState('');
  const [extraDraft, setExtraDraft] = React.useState('');
  const [savingText, setSavingText] = React.useState(false);
  const seededRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!placementQuery.data || seededRef.current === orderLocalId) return;
    setDebrisDraft(placement?.debris_description ?? '');
    setExtraDraft(placement?.notes ?? '');
    seededRef.current = orderLocalId ?? null;
  }, [placementQuery.data, orderLocalId, placement]);

  // PUT /orders/<localId> with just the placement keys that changed.
  const savePlacement = async (patch: Record<string, unknown>) => {
    if (!orderLocalId) return;
    setSavingText(true);
    try {
      const res = await apiFetch(`/orders/${encodeURIComponent(orderLocalId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Save failed (HTTP ${res.status})`);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.localRow(orderLocalId!) });
    } catch (err: any) {
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn't save customer notes</ToastTitle>
          <ToastBody>{err?.message || 'Please try again.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setSavingText(false);
    }
  };
  // Text fields autosave on blur, only when the value actually changed.
  const handleDebrisBlur = () => {
    if (debrisDraft === (placement?.debris_description ?? '')) return;
    savePlacement({ debris_description: debrisDraft.trim() || null });
  };
  const handleExtraBlur = () => {
    if (extraDraft === (placement?.notes ?? '')) return;
    savePlacement({ notes: extraDraft.trim() || null });
  };

  const saveAck = useMutation({
    mutationFn: async (checked: boolean) => {
      const res = await apiFetch(`/orders/${encodeURIComponent(orderLocalId!)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prohibited_items_acknowledged: checked }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Save failed (HTTP ${res.status})`);
      }
      return checked;
    },
    onMutate: async (checked: boolean) => {
      const key = queryKeys.orders.localRow(orderLocalId!);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<{ data?: PlacementData }>(key);
      queryClient.setQueryData<{ data?: PlacementData }>(key, (old) =>
        old?.data
          ? { ...old, data: { ...old.data, prohibited_items_acknowledged: checked } }
          : old,
      );
      return { previous };
    },
    onError: (err: any, _checked, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.orders.localRow(orderLocalId!), context.previous);
      }
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn't save acknowledgment</ToastTitle>
          <ToastBody>{err?.message || 'Please try again.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.localRow(orderLocalId!) });
    },
  });

  // Nothing to show: new/unsaved order or still loading.
  if (!orderLocalId || placementQuery.isLoading) return null;
  // Read-only orders (website / sync) with nothing recorded render nothing —
  // there's no customer submission to display and no admin gate to satisfy.
  const hasAckState = acknowledged === true || acknowledged === false;
  const hasAnyContent =
    debrisDescription.length > 0 || additionalInfo.length > 0 || hasAckState;
  if (!editable && !hasAnyContent) return null;

  return (
    <div className={styles.card}>
      <Toaster toasterId={toasterId} />
      <Text weight="semibold" size={300} className={styles.cardTitle}>
        Customer Notes
      </Text>

      {editable ? (
        <>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Dump description</span>
            <Textarea
              className={styles.textarea}
              value={debrisDraft}
              onChange={(_, d) => setDebrisDraft(d.value)}
              onBlur={handleDebrisBlur}
              placeholder="What is the customer disposing of?"
              resize="vertical"
              disabled={savingText}
            />
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Additional info</span>
            <Textarea
              className={styles.textarea}
              value={extraDraft}
              onChange={(_, d) => setExtraDraft(d.value)}
              onBlur={handleExtraBlur}
              placeholder="Extra details (optional)"
              resize="vertical"
              disabled={savingText}
            />
          </div>
        </>
      ) : (
        <>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Dump description</span>
            {debrisDescription ? (
              <span className={styles.fieldValue}>{debrisDescription}</span>
            ) : (
              <span className={styles.fieldValueEmpty}>Not provided</span>
            )}
          </div>
          {additionalInfo && (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Additional info</span>
              <span className={styles.fieldValue}>{additionalInfo}</span>
            </div>
          )}
        </>
      )}

      {/* Prohibited-items gate — kept below a divider, in its own sub-block, so
          the reservation gate stays visually weightier than the notes above. */}
      <div className={styles.divider} />
      <div className={styles.ackBlock}>
        <div className={styles.ackHeaderRow}>
          <Text weight="semibold" size={300}>
            Prohibited items
            {editable && !acknowledged && (
              <span className={styles.requiredAsterisk}> *</span>
            )}
          </Text>
          {editable && !acknowledged ? (
            <Tooltip
              content="This order can’t be reserved until the customer acknowledges the prohibited-items list."
              relationship="label"
            >
              <Info16Regular tabIndex={0} className={styles.infoIcon} />
            </Tooltip>
          ) : null}
        </div>

        {editable ? (
          <ProhibitedItemsAck
            acknowledged={acknowledged}
            onChange={(checked) => saveAck.mutate(checked)}
            disabled={saveAck.isPending}
          />
        ) : acknowledged === true ? (
          // Website orders: checkout blocks until the box is checked, so this is
          // a quiet audit line confirming the customer acknowledged at booking.
          <div className={styles.auditNote}>
            <CheckmarkCircle16Filled className={styles.auditIcon} />
            Prohibited-items list acknowledged at booking
          </div>
        ) : hasAckState ? (
          // Defensive fallback — website orders can't normally reach an unchecked
          // state, but render it honestly if one ever does.
          <div className={styles.notAcked}>
            <DismissCircle16Filled style={{ color: tokens.colorPaletteRedForeground1 }} />
            Not acknowledged
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OrderCustomerNotesSection;
